import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApplicationContext } from '~/shared/context/use-application-context';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { updateLevelDetails, updateRoleDetails, updateVersionsData } from './operations';
import { jobArchitectureKeys } from './query-keys';

export type UseReviewJobItemVersionResult = UseMutationResult<
    unknown,
    Error,
    {
        readonly action: 'review' | 'unreview';
        readonly architectureId: string;
        readonly itemId: string;
        readonly itemType: 'job-level' | 'job-role';
        readonly version: string;
    }
>;

export const useReviewJobItemVersion = (): UseReviewJobItemVersionResult => {
    const { reviewJobLevelVersion, reviewJobRoleVersion, unreviewJobLevelVersion, unreviewJobRoleVersion } =
        useApplicationFetchers();
    const queryClient = useQueryClient();
    const { applicationState } = useApplicationContext();
    const { myUserProfile } = applicationState;
    const myUserProfileData = myUserProfile.unwrapOrElse(() => undefined);

    return useMutation({
        mutationFn: async ({ action, architectureId, itemId, itemType, version }) => {
            // biome-ignore lint/suspicious/noImplicitAnyLet: suppressed due to migration
            let result;

            if (itemType === 'job-level') {
                result =
                    action === 'review'
                        ? await reviewJobLevelVersion({
                              jobArchitectureId: architectureId,
                              jobLevelId: itemId,
                              version,
                          })
                        : await unreviewJobLevelVersion({
                              jobArchitectureId: architectureId,
                              jobLevelId: itemId,
                              version,
                          });
            } else {
                result =
                    action === 'review'
                        ? await reviewJobRoleVersion({
                              jobArchitectureId: architectureId,
                              jobRoleId: itemId,
                              version,
                          })
                        : await unreviewJobRoleVersion({
                              jobArchitectureId: architectureId,
                              jobRoleId: itemId,
                              version,
                          });
            }

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: async ({ action, architectureId, itemId, itemType, version }) => {
            const versionsQueryKey =
                itemType === 'job-level'
                    ? jobArchitectureKeys.levelVersions(architectureId, itemId)
                    : jobArchitectureKeys.roleVersions(architectureId, itemId);

            const detailQueryKey =
                itemType === 'job-level'
                    ? jobArchitectureKeys.level(architectureId, itemId)
                    : jobArchitectureKeys.role(architectureId, itemId);

            await Promise.all([
                queryClient.cancelQueries({ queryKey: versionsQueryKey }),
                queryClient.cancelQueries({ queryKey: detailQueryKey }),
            ]);

            updateVersionsData({
                action,
                architectureId,
                itemId,
                itemType,
                queryClient,
                version,
            });

            if (itemType === 'job-role') {
                updateRoleDetails({
                    action,
                    architectureId,
                    itemId,
                    myUserProfileData,
                    queryClient,
                    version,
                });
            } else {
                updateLevelDetails({
                    action,
                    architectureId,
                    itemId,
                    queryClient,
                    version,
                });
            }
        },
        onSuccess: (_, { architectureId }) => {
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architecture(architectureId),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architectureDetail(architectureId),
            });
        },
    });
};
